/* eslint-disable no-underscore-dangle */
/* eslint-disable no-restricted-syntax */
/* eslint-disable react/jsx-no-bind */
import lod_ from "lodash";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Step,
	StepLabel,
	Stepper
} from "@mui/material";
import { useRef, useState } from "react";
import MDButton from "components/Basics/MDButton";
import MDBox from "components/Basics/MDBox";
import i18n from "i18n";
import StepImport from "./1. Import";
import { useDispatch } from "react-redux";
import { t } from "i18next";

import sourceActions from "redux-react/actions/sourceActions";
import { createAssets } from "redux-react/reducers/assetReducer";
import AddAssetStepSelectFiles from "../components/AddAssetStepSelectFiles";

/**
 * Add sources dialog
 * User import files from different sources
 * @param {*} param0
 * @returns {JSX.Element} AddSourceDialog
 */
export default function AddExternalPage({
	open,
	onClose,
	format = "WebPage",
	folderId,
	getFolders = () => {}
}) {
	const dispatch = useDispatch();
	const topContainer = useRef(null);

	const [importing, setImporting] = useState(false);

	/**
	 * Active step
	 */
	const [activeStep, setActiveStep] = useState(0);

	/**
	 * Steps labels
	 */
	const [steps, setSteps] = useState([t("SOURCES.STEPS.upload"), t("SOURCES.STEPS.filesChoice")]);
	/**
	 * Can user go to next step
	 */
	const [stepValid, setStepValid] = useState(false);

	const [skeleton, setSkeleton] = useState({
		selectedRows: [], // Selected rows in table
		files: [] // Imported files
	});

	/**
	 * Close dialog
	 */
	function close() {
		setImporting(false);
		setActiveStep(0);
		setStepValid(false);
		setSkeleton({
			selectedRows: [],
			files: []
		});
		onClose();
	}

	/**
	 * User can go to next step
	 */
	const validStep = (val = true) => {
		setStepValid(val);
	};

	/**
	 * Go to next step
	 */
	const handleNext = () => {
		setActiveStep(activeStep + 1);
		setStepValid(false);
		topContainer?.current?.scrollIntoView();
	};

	/**
	 * Transform files informations to correct files format
	 * @param {Array<object>} files - Files to import
	 */
	const handleSaveFilesSelection = files => {
		setSkeleton(skeleton => {
			return {
				...skeleton,
				files
			};
		});

		validStep();
		handleNext();
	};

	/**
	 * Update files
	 * @param {Array<object>} files - Updated files
	 */
	const onChangeFiles = files => {
		setSkeleton(skeleton => {
			return {
				...skeleton,
				files
			};
		});
	};

	const importExternalPages = () => {
		setImporting(true);
		const files = skeleton.files.filter(file => skeleton.selectedRows.includes(file.url));

		const filesPaylaod = [];

		const getFormat = () => {
			switch (format) {
				case "wordpressPage":
				case "sitemap": {
					return "WebPage"; // Transform into webpage
				}
				case "shopifyPage": {
					return "htmlText"; // Transform into htmlText
				}
				default: {
					return format;
				}
			}
		};

		for (let file of files) {
			const fileType = getFormat();
			let payload = {
				name: file.name,
				folderId,
				fileType,
				language: file.language,
				alreadyExists: file.alreadyExists,
				content: file.url,
				metadatas: file.metadatas || {}
			};

			switch (fileType) {
				case "htmlText": {
					// Create HTML content for shopify:
					const shopifyContent = lod_.get(file, "extra.rawText", "");
					lod_.set(payload, "content", shopifyContent);
					break;
				}
				default: {
					break;
				}
			}

			lod_.set(payload, "metadatas.image", file.image || null);

			filesPaylaod.push(payload);
		}

		const onSuccessCallback = assets => {
			const formattedAssets = assets.map(asset => ({ ...asset, id: asset._id }));
			dispatch(createAssets(formattedAssets));
			getFolders();
			close();
		};

		dispatch(sourceActions.createManyAssets({ files: filesPaylaod }, onSuccessCallback));
	};

	/**
	 * Get actual step content
	 */
	function getStepContent(stepIndex) {
		switch (stepIndex) {
			case 0: {
				return (
					<StepImport
						format={format}
						skeleton={skeleton}
						handleSave={handleSaveFilesSelection}
						validStep={validStep}
					/>
				);
			}
			case 1: {
				return (
					<AddAssetStepSelectFiles
						format={format}
						skeleton={skeleton}
						setSkeleton={setSkeleton}
						onChange={onChangeFiles}
						validStep={validStep}
					/>
				);
			}
			default:
				return null;
		}
	}

	const getNextButton = () => {
		switch (activeStep) {
			case 0:
				return null;
			case 1: {
				return (
					<MDButton
						disabled={!stepValid || skeleton.selectedRows.length < 1 || importing}
						variant="contained"
						color="info"
						onClick={importExternalPages}
					>
						{`${t("SETTINGS.add")} (${skeleton.selectedRows.length}) ${t("SOURCES.nextFiles")}`}
					</MDButton>
				);
			}
			default:
				return null;
		}
	};

	return (
		<Dialog
			fullWidth
			maxWidth="xxl"
			PaperProps={{
				sx: {
					height: "90%"
				}
			}}
			open={open}
			onClose={close}
		>
			<DialogTitle>
				<MDBox>
					<Stepper activeStep={activeStep} alternativeLabel>
						{steps.map(label => (
							<Step key={label}>
								<StepLabel>{label}</StepLabel>
							</Step>
						))}
					</Stepper>
				</MDBox>
			</DialogTitle>
			<DialogContent>
				<div data-id="top-container" ref={topContainer}></div>
				<MDBox style={{ height: "100%" }}>{getStepContent(activeStep)}</MDBox>
			</DialogContent>
			<DialogActions>
				<MDButton variant="outlined" color="info" onClick={close}>
					{i18n.t("SETTINGS.cancel")}
				</MDButton>
				{activeStep > 0 && (
					<MDButton
						variant="contained"
						color="light"
						onClick={() => {
							setActiveStep(activeStep - 1);
						}}
					>
						{t("SETTINGS.back")}
					</MDButton>
				)}
				{getNextButton()}
			</DialogActions>
		</Dialog>
	);
}
